import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDialogDataContext } from "@/providers/DialogDataProvider";
import { DialogWizard } from   "@/components/wizards/dialog/DialogWizard";
import { Dialog, updateDialog, createDialog } from "@/lib/stores/dialogStore";
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';
import { useTabContext } from '@/contexts/TabContext';
import { useTabTitleSync } from '@/hooks/useTabTitleSync';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useSplitViewAwareParams } from '@/hooks/useSplitViewAwareParams';
import { useSplitViewContext } from '@/components/layout/SplitViewContent';
import { useAutoSave } from '@/hooks/useAutoSave';

export default function DialogItemPage() {
  const { dialogId } = useSplitViewAwareParams<{ dialogId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { updateTabTitle, updateTabUnsavedChanges, requestTabClose, tabs } = useTabContext();
  const splitViewContext = useSplitViewContext();
  const isSplitView = splitViewContext?.isSplitView ?? false;
  const { dialogs, isLoading: contextLoading, refreshDialogs, loadData } = useDialogDataContext();
  const { selectedGame } = useSimplifiedUserData();
  
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const hasTriedLoading = React.useRef(false);

  // Track unsaved changes and save handler for auto-save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveHandlerRef = useRef<(() => Promise<void>) | undefined>();

  // Check if this is a new dialog creation
  const isNewDialog = dialogId === 'new';
  
  // Get folder from URL params for new dialogs
  const folderFromUrl = searchParams.get('folder');
  
  // Sync tab title with dialog name (handles both initial load and name changes)
  // Skip in split view mode - split view tabs are managed separately
  useTabTitleSync(
    !isNewDialog ? dialog?.name : undefined, 
    { disabled: isSplitView }
  );

  // Generate default new dialog data
  const newDialogData = useMemo(() => {
    if (!isNewDialog || !selectedGame?.id) return null;
    
    // Count existing dialogs to generate unique name
    const existingNewDialogs = dialogs.filter(d => d.name.startsWith('New Dialog'));
    const nextNumber = existingNewDialogs.length > 0 ? existingNewDialogs.length + 1 : 1;
    const dialogName = nextNumber > 1 ? `New Dialog ${nextNumber}` : 'New Dialog';
    const dialogKey = dialogName.toLowerCase().replace(/\s+/g, '_');
    return {
      game_id: selectedGame.id,
      name: dialogName,
      dialog_key: dialogKey,
      description: '',
      tags: [],
      folder: folderFromUrl || null,
      status: 'draft' as const,
      owner: null,
      edit_permissions: 'Open' as const,
      data: {
        sections: [{
          id: `section_${Date.now()}`,
          name: 'Section 1',
          section_identifier: 'section_1',
          order_index: 0,
          fields: [{
            id: `field_${Date.now()}_1`,
            type: 'dialog' as const,
            text: '',
            translatable: true,
            order_index: 0,
          }],
          fieldOrder: [`field_${Date.now()}_1`],
        }],
        translatable: true,
      },
    };
  }, [isNewDialog, selectedGame?.id, dialogs, folderFromUrl]);

  // Reset state immediately when dialogId changes to prevent showing stale data
  useEffect(() => {
    if (!isNewDialog) {
      setDialog(null);
      setIsLoading(true);
      setNotFound(false);
      hasTriedLoading.current = false;
    }
  }, [dialogId, isNewDialog]);

  // Handle new dialog setup
  useEffect(() => {
    if (isNewDialog && newDialogData && !isSplitView) {
      // Set up tab title for new dialog
      const tabId = location.pathname;
      updateTabTitle(tabId, newDialogData.name);
      setIsLoading(false);
    }
  }, [isNewDialog, newDialogData, location.pathname, updateTabTitle, isSplitView]);

  // Load existing dialog
  useEffect(() => {
    if (isNewDialog || !dialogId) {
      if (!isNewDialog && !dialogId) {
        setNotFound(true);
        setIsLoading(false);
      }
      return;
    }

    // Try to find the item first - it might already be in the store/cache
    const item = dialogs.find(d => d.id === dialogId);
    
    if (item) {
      // Update if item changed (compare updated_at timestamps)
      setDialog(prev => {
        if (!prev || prev.id !== item.id || prev.updated_at !== item.updated_at) {
          return item;
        }
        return prev;
      });
      setNotFound(false);
      setIsLoading(false);
    } else if (contextLoading) {
      // Item not found but still loading, wait
      setIsLoading(true);
    } else if (!hasTriedLoading.current) {
      // Item not found and loading completed, trigger a load once
      // This only happens when navigating directly to a URL without cached data
      hasTriedLoading.current = true;
      loadData();
    } else {
      // Already tried loading and item still not found
      console.warn(`Dialog ${dialogId} not found in loaded items`);
      setNotFound(true);
      setIsLoading(false);
    }
  }, [dialogId, dialogs, contextLoading, isNewDialog, loadData]);

  // Redirect to dialogs page if item not found (only for existing dialogs)
  useEffect(() => {
    if (notFound && !isLoading && !isNewDialog) {
      console.warn(`Dialog ${dialogId} not found or access denied. Redirecting to dialogs.`);
      navigate('/dialogs', { replace: true });
    }
  }, [notFound, isLoading, dialogId, navigate, isNewDialog]);

  // Tab title sync is now handled by useTabTitleSync hook above

  // Log dialog info only once when it loads (not on every render)
  // useEffect(() => {
  //   if (dialog && dialogId && !isNewDialog) {
  //     console.log('Dialog loaded:', dialog);
  //   }
  // }, [dialog?.id]); // Only re-run if dialog ID changes

  // Handle saving a new dialog (create)
  const handleSave = useCallback(async (dialogData: any) => {
    if (!selectedGame?.id) return;

    try {
      const newDialog = await createDialog(
        {
          ...dialogData,
          game_id: selectedGame.id,
        },
        toast
      );

      // Note: No need to refresh - createDialog already updates the store,
      // and the DialogDataProvider subscription will handle UI updates

      toast({
        title: 'Success',
        description: 'Dialog created successfully',
      });

      // Clear tab unsaved changes BEFORE navigation to prevent stale hasUnsavedChanges
      // from persisting through the wizard remount (key change causes old wizard to unmount,
      // losing its setTimeout cleanup, while the tab retains the old unsaved changes flag)
      const activeTab = tabs.find(t => t.isActive);
      if (activeTab) {
        updateTabUnsavedChanges(activeTab.id, false);
      }

      // Navigate to the newly created dialog page
      navigate(`/dialogs/${newDialog.id}`, { replace: true });

      // Return the new dialog so the wizard can use it if needed
      return newDialog;
    } catch (error) {
      console.error('Error creating dialog:', error);
      toast({
        title: 'Error',
        description: 'Failed to create dialog',
        variant: 'destructive',
      });
      throw error; // Re-throw so the wizard knows the creation failed
    }
  }, [selectedGame?.id, toast, navigate, tabs, updateTabUnsavedChanges]);

  // Handle updating an existing dialog
  const handleUpdate = useCallback(async (updatedItem: any) => {
    if (!dialogId || !dialog) return;

    try {
      const updatedDialog = await updateDialog(
        {
          ...updatedItem,
          id: dialogId,
          game_id: dialog.game_id,
        },
        toast
      );

      // Note: No need to refresh - updateDialog already updates the store,
      // and the DialogDataProvider subscription will handle UI updates

      // Tab title update is handled by useTabTitleSync hook

      toast({
        title: 'Success',
        description: 'Dialog updated successfully',
      });

      // Return the updated dialog so the wizard can refresh its local state
      return updatedDialog;
    } catch (error) {
      console.error('Error updating dialog:', error);
      toast({
        title: 'Error',
        description: 'Failed to update dialog',
        variant: 'destructive',
      });
      throw error; // Re-throw so the wizard knows the update failed
    }
  }, [dialogId, dialog, toast, location.pathname, updateTabTitle]);

  const handleClose = useCallback(async () => {
    // Find the ACTUAL tab ID by looking for the active tab
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) {
      navigate('/dialogs');
      return;
    }
    
    const tabId = activeTab.id;
    
    // Request to close the tab (will show unsaved changes dialog if needed)
    const canClose = await requestTabClose(tabId);
    
    if (canClose) {
      // If the tab was closed, navigate to dialogs page
      navigate('/dialogs');
    }
    // If canClose is false, user cancelled the close operation
  }, [tabs, requestTabClose, navigate]);

  // Handle unsaved changes state from wizard
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean, saveHandler: () => Promise<void>, discardHandler?: () => void) => {
    // Find the ACTUAL tab ID by looking for the active tab
    // Tab IDs may be stable IDs like 'tab-dialogs' rather than the pathname
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return;
    
    const tabId = activeTab.id;
    // Track for auto-save
    setHasUnsavedChanges(hasChanges);
    saveHandlerRef.current = saveHandler;
    // Update tab context
    updateTabUnsavedChanges(tabId, hasChanges, saveHandler, discardHandler);
  }, [tabs, updateTabUnsavedChanges]);

  // Enable auto-save based on user settings
  useAutoSave({
    hasUnsavedChanges,
    saveHandler: saveHandlerRef.current,
    enabled: !isNewDialog, // Only auto-save for existing dialogs, not new ones
  });

  // Show loading state (only for existing dialogs)
  if (!isNewDialog && (isLoading || contextLoading)) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading dialog..." />
      </div>
    );
  }

  // For new dialogs, check if we have required data
  if (isNewDialog) {
    if (!selectedGame?.id) {
      return (
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="lg" text="Loading..." />
        </div>
      );
    }

    // Render wizard for new dialog
    return (
      <div className="h-full w-full bg-surface">
        <DialogWizard
          key="dialog-new"
          inline={true}
          onClose={handleClose}
          initialData={newDialogData as any}
          gameId={selectedGame.id}
          onSave={handleSave}
          onUnsavedChangesChange={handleUnsavedChangesChange}
        />
      </div>
    );
  }

  // If notFound or no dialog, show loading while redirecting (redirect happens in useEffect)
  if (notFound || !dialog) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Redirecting..." />
      </div>
    );
  }

  // CRITICAL: Ensure the dialog ID matches the URL dialogId before rendering
  // This prevents showing stale data when the dialog state hasn't updated yet
  if (dialog.id !== dialogId) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading dialog..." />
      </div>
    );
  }

  // Create a stable key to force remount when switching dialogs
  const wizardKey = `dialog-${dialogId}`;

  return (
    <div className="h-full w-full bg-surface" style={{pointerEvents: 'auto'}}>
      <DialogWizard
        key={wizardKey}
        inline={true}
        onClose={handleClose}
        initialData={dialog}
        onUpdate={handleUpdate}
        onUnsavedChangesChange={handleUnsavedChangesChange}
      />
    </div>
  );
}
